import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Save, Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setForm(data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { company_name, company_name_ar, trn, address, phone, email, footer_note } = form;
      await api.put("/settings", { company_name, company_name_ar, trn, address, phone, email, footer_note });
      toast.success("Company settings saved — used on every voucher PDF");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight flex items-center gap-2">
          <Building2 className="w-5 h-5" /> Company Settings
        </h2>
        <p className="text-sm text-slate-500">These details appear on the voucher header and PDF export.</p>
      </div>

      <Card className="p-5 md:p-6 border-slate-200 dark:border-slate-800 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Company name (EN)</Label>
            <Input value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} className="mt-1.5" data-testid="settings-company-name" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Company name (AR)</Label>
            <Input value={form.company_name_ar || ""} onChange={(e) => set("company_name_ar", e.target.value)} className="mt-1.5" dir="rtl" data-testid="settings-company-name-ar" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">TRN (Tax Registration No.)</Label>
            <Input value={form.trn || ""} onChange={(e) => set("trn", e.target.value)} className="mt-1.5 font-mono" data-testid="settings-trn" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Phone</Label>
            <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" data-testid="settings-phone" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
            <Input value={form.email || ""} onChange={(e) => set("email", e.target.value)} className="mt-1.5" data-testid="settings-email" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Address</Label>
            <Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} className="mt-1.5" data-testid="settings-address" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Footer note (PDF)</Label>
          <Textarea value={form.footer_note || ""} onChange={(e) => set("footer_note", e.target.value)} className="mt-1.5" data-testid="settings-footer" />
        </div>
        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={saving} className="gap-2" data-testid="save-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save settings
          </Button>
        </div>
      </Card>
    </div>
  );
}
